/**
 * Cross-feature strings only. Every string a single surface owns lives in that
 * feature's own `copy.ts` — there is no `src/copy/` in any Shahedni frontend
 * (`frontend-layout.md` §10.4), and no Arabic string is inlined in JSX.
 *
 * The copy addresses the operator, never the reviewer: no design rationale, no
 * technical vocabulary, no restatement of a mechanic the screen already
 * performs. The test before shipping a hint is whether the operator can act
 * differently after reading it.
 */
export const copy = {
  skipToContent: 'تخطَّ إلى المحتوى',

  action: {
    retry: 'أعد المحاولة',
    cancel: 'إلغاء',
    confirm: 'تأكيد',
    close: 'إغلاق',
    signOut: 'تسجيل الخروج',
    copy: 'نسخ',
    copied: 'نُسخ',
  },

  /**
   * Signing out of the console is CONFIRMED, and the confirmation is `warning`
   * rather than `danger`: nothing is destroyed by it. What the body has to say
   * is the operator-specific consequence — the console host holds its own
   * cookie, so leaving here does not touch a subscriber session on the app
   * host, and any half-finished form on screen is not saved.
   */
  signOutConfirm: {
    heading: 'الخروج من لوحة التحكّم',
    body: 'تنتهي جلسة لوحة التحكّم على هذا الجهاز. أيّ نموذج مفتوح لم يُحفظ بعد سيضيع، وحسابك وصلاحياتك تبقى كما هي.',
    commitLabel: 'سجّل الخروج',
  },

  theme: {
    label: 'الوضع',
    toDark: 'الوضع الداكن',
    toLight: 'الوضع الفاتح',
  },

  state: {
    loading: 'جارٍ التحميل',
    loaded: 'اكتمل التحميل',
    offline: 'لا اتّصال — أعد المحاولة بعد عودة الشبكة',
    stale: 'بيانات محفوظة من آخر تحميل ناجح',
  },

  error: {
    title: 'تعذّر إتمام الطلب',
    /**
     * A refusal on the caller's role is **not** a failed request, and saying so
     * misdescribes it three times over: nothing was attempted and failed, the
     * index was never going to change, and «أعد المحاولة» offers an operator a
     * button that cannot succeed however many times they press it. It gets its
     * own title, drops the unaffected line, and carries no retry.
     */
    permissionTitle: 'هذا القسم خارج صلاحيّتك',
    unaffected: 'لم يتغيّر شيء في الفهرس.',
    /**
     * The next step for a role refusal. A role is **granted from the console**
     * and never requested — catalog §20.19 — so the sentence names where it
     * comes from rather than telling the operator to ask for one.
     */
    permissionNextStep: 'يمنحك مسؤولٌ هذا الدور من صفحة المستخدمين.',
  },

  dialog: {
    /** A destructive confirmation always names its subject; this is the frame. */
    irreversible: 'لا يمكن التراجع عن هذا الإجراء.',
    restorable: 'يُرفع من القرّاء ويبقى قابلاً للاستعادة.',
  },

  /**
   * The traversal control's frame. Cross-feature because four operator lists
   * walk pages and each names its own rows — the surface owns the noun it
   * counts and the label of its own `<nav>`; the frame around them is one.
   *
   * `of` joins two figures and is never dropped: «صفحة ٢ ٧» reads as one
   * three-digit number, which is the misreading bidirectional isolation exists
   * to prevent and cannot fix on its own.
   */
  pager: {
    previous: 'السابق',
    next: 'التالي',
    pageLabel: 'صفحة',
    of: 'من',
    /**
     * The elision in a long ordinal strip. It is drawn `aria-hidden`, because a
     * screen reader is told the page and the count in the status line above and
     * has no use for a mark that stands in for numbers it was never going to
     * read out.
     */
    gap: '…',
  },
} as const;
